import type { OutboxOp } from './store';

/** Attempt budget. The next failure after this many recorded attempts is dead. */
export const MAX_OUTBOX_ATTEMPTS = 8;
export const MAX_BACKOFF_MS = 30 * 60 * 1000;

export type OutboxDisposition = 'ok' | 'retry' | 'pause' | 'dead';

/**
 * Delay before the next try. `attempts` is the count already recorded.
 * 2^n seconds, capped at 30 minutes.
 */
export function backoffMs(attempts: number): number {
  const exponent = Math.min(Math.max(0, attempts), 20);
  return Math.min(1000 * 2 ** exponent, MAX_BACKOFF_MS);
}

export function classifyOutboxFailure(input: {
  status?: number;
  body?: string;
  attempts: number;
  networkError?: boolean;
}): Exclude<OutboxDisposition, 'ok'> {
  if (input.status === 401) {
    return 'pause';
  }
  const nextAttempts = input.attempts + 1;
  const overBudget = nextAttempts > MAX_OUTBOX_ATTEMPTS;
  if (input.networkError) {
    return overBudget ? 'dead' : 'retry';
  }
  const status = input.status ?? 0;
  const body = input.body ?? '';
  const uuidCollision = status === 409 && body.includes('UUID_COLLISION');
  if (status === 400 || status === 413 || uuidCollision || overBudget) {
    return 'dead';
  }
  return 'retry';
}

/** A comment or reaction waits while the op it depends on is still in the outbox. */
export function isBlockedByDependency(
  op: Pick<OutboxOp, 'dependsOn'>,
  byId: ReadonlyMap<string, Pick<OutboxOp, 'status'>>
): boolean {
  if (!op.dependsOn) {
    return false;
  }
  const dependency = byId.get(op.dependsOn);
  if (!dependency) {
    return false;
  }
  return dependency.status === 'pending' || dependency.status === 'dead';
}
